import { LANGS, LANG_META, DEFAULT_OG_IMAGE, urlFor, routeLangs, isIndexableIn, pathFor, type Lang } from "./routes";
import { findRoute } from "./allRoutes";
import { buildJsonLd, type ArticleLd, type FaqItem } from "./jsonld";

export interface HeadData {
  lang: Lang;
  basePath: string; // without lang, e.g. "/retail" or ""
  title: string;
  name: string; // title without brand
  description: string;
  keywords?: string;
  ogImage?: string;
  ogType?: string;
  noIndex?: boolean;
  /** Publishable FAQ items (already filtered) for FAQPage JSON-LD. */
  faq?: FaqItem[];
  /** Resources/cases: Article JSON-LD data. */
  article?: ArticleLd;
}

/** Set during server rendering by SEOHead so the prerender can read it. */
export const ssrHead: { current: HeadData | null } = { current: null };

const esc = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export interface HeadOptions {
  /** Unprefixed legacy URL (e.g. /retail): noindex,follow + canonical to the /es/ version, no hreflang/JSON-LD. */
  legacy?: boolean;
}

export function headHtml(d: HeadData, opts: HeadOptions = {}): string {
  const route = findRoute(d.basePath);
  const path = route?.path ?? d.basePath.replace(/^\/+/, "");
  if (opts.legacy) return legacyHeadHtml(d, path, d.ogImage || route?.ogImage || DEFAULT_OG_IMAGE);
  const draft = !!route?.draft;
  // Untranslated language: page works (EN text) but points search engines to the EN version.
  const untranslated = !d.noIndex && !draft && !isIndexableIn(route, d.lang);
  const altLangs = route ? routeLangs(route) : [...LANGS];
  const at = (l: Lang) => urlFor(l, route ? pathFor(route, l) : path);
  // Drafts canonicalise to themselves (or to the EN/ES file their language falls back to).
  const canonicalLang: Lang = untranslated || (draft && !altLangs.includes(d.lang))
    ? (altLangs.includes("en") ? "en" : altLangs[0] ?? "es")
    : d.lang;
  const url = at(canonicalLang);
  const image = d.ogImage || route?.ogImage || DEFAULT_OG_IMAGE;
  const robots = d.noIndex
    ? "noindex, nofollow"
    : untranslated || draft
      ? "noindex, follow"
      : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
  const showAlternates = !d.noIndex && !untranslated && !draft;
  const lines = [
    `<title>${esc(d.title)}</title>`,
    `<meta name="description" content="${esc(d.description)}">`,
    d.keywords ? `<meta name="keywords" content="${esc(d.keywords)}">` : "",
    `<meta name="robots" content="${robots}">`,
    `<meta name="googlebot" content="${robots}">`,
    `<link rel="canonical" href="${url}">`,
    ...(showAlternates
      ? [
          ...altLangs.map((l) => `<link rel="alternate" hreflang="${LANG_META[l].hreflang}" href="${at(l)}">`),
          `<link rel="alternate" hreflang="x-default" href="${at("es")}">`,
        ]
      : []),
    `<meta property="og:type" content="${d.ogType || "website"}">`,
    `<meta property="og:site_name" content="Techtail">`,
    `<meta property="og:url" content="${url}">`,
    `<meta property="og:title" content="${esc(d.title)}">`,
    `<meta property="og:description" content="${esc(d.description)}">`,
    `<meta property="og:image" content="${esc(image)}">`,
    `<meta property="og:image:width" content="1200">`,
    `<meta property="og:image:height" content="630">`,
    `<meta property="og:locale" content="${LANG_META[d.lang].locale}">`,
    ...altLangs.filter((l) => l !== d.lang).map(
      (l) => `<meta property="og:locale:alternate" content="${LANG_META[l].locale}">`,
    ),
    `<meta name="twitter:card" content="summary_large_image">`,
    `<meta name="twitter:title" content="${esc(d.title)}">`,
    `<meta name="twitter:description" content="${esc(d.description)}">`,
    `<meta name="twitter:image" content="${esc(image)}">`,
    ...(!showAlternates
      ? []
      : buildJsonLd(d.lang, d.basePath, d.name, d.description, d.faq, d.article).map(
          (b) =>
            `<script type="application/ld+json" data-seo="page">${JSON.stringify(b).replace(/</g, "\\u003c")}</script>`,
        )),
  ];
  return lines.filter(Boolean).join("\n    ");
}

/** Head for unprefixed legacy URLs: points search engines to the /es/ page and keeps them out of the index. */
function legacyHeadHtml(d: HeadData, path: string, image: string): string {
  const url = urlFor("es", path);
  return [
    `<title>${esc(d.title)}</title>`,
    `<meta name="description" content="${esc(d.description)}">`,
    `<meta name="robots" content="noindex, follow">`,
    `<meta name="googlebot" content="noindex, follow">`,
    `<link rel="canonical" href="${url}">`,
    `<meta property="og:type" content="website">`,
    `<meta property="og:site_name" content="Techtail">`,
    `<meta property="og:url" content="${url}">`,
    `<meta property="og:title" content="${esc(d.title)}">`,
    `<meta property="og:description" content="${esc(d.description)}">`,
    `<meta property="og:image" content="${esc(image)}">`,
    `<meta property="og:locale" content="${LANG_META.es.locale}">`,
    `<meta name="twitter:card" content="summary_large_image">`,
  ].join("\n    ");
}
